<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"

const props = defineProps({
  offcanvasId: { type: String, required: true },
  analyzeUrl: { type: String, required: true },
  statusUrl: { type: String, required: true },
  applyAllUrl: { type: String, required: true },
  titleLabel: { type: String, default: "AI Analysis" },
  rerunLabel: { type: String, default: "Re-run" },
  stateQueuedLabel: { type: String, default: "Queued" },
  stateProcessingLabel: { type: String, default: "Analyzing" },
  stateCompletedLabel: { type: String, default: "Completed" },
  stateFailedLabel: { type: String, default: "Failed" },
  stateIdleLabel: { type: String, default: "Idle" },
  loadingLabel: { type: String, default: "Analyzing guide. Please wait..." },
  noSuggestionsLabel: { type: String, default: "No suggestions found." },
  applyAllLabel: { type: String, default: "Apply All" },
  applyLabel: { type: String, default: "Apply" },
  dismissLabel: { type: String, default: "Dismiss" },
  currentTextLabel: { type: String, default: "Current" },
  suggestedTextLabel: { type: String, default: "Suggested" },
  stepLabel: { type: String, default: "Step" },
})

const aiState = ref("idle")
const suggestions = ref([])
const errorMessage = ref("")
const isLoading = ref(false)
const isApplyingAll = ref(false)
const actionError = ref("")
const shouldAutoStart = ref(false)

let offcanvasElement = null
let pollTimer = null

const isAnalyzing = computed(() => aiState.value === "queued" || aiState.value === "processing")
const canApplyAll = computed(() => aiState.value === "completed" && suggestions.value.length > 0 && !isApplyingAll.value)
const canRerun = computed(() => !isLoading.value && !isAnalyzing.value)

const stateLabel = computed(() => {
  if (aiState.value === "queued") return props.stateQueuedLabel
  if (aiState.value === "processing") return props.stateProcessingLabel
  if (aiState.value === "completed") return props.stateCompletedLabel
  if (aiState.value === "failed") return props.stateFailedLabel
  return props.stateIdleLabel
})

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content || ""
}

function urlWithParams(url, params = {}) {
  const u = new URL(url, window.location.origin)
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) u.searchParams.set(key, String(value))
  })
  return u.toString()
}

async function postJson(url) {
  return fetch(url, {
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken(),
      Accept: "application/json",
    },
    credentials: "same-origin",
  })
}

async function getJson(url) {
  return fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
    },
    credentials: "same-origin",
  })
}

function updateFromPayload(payload) {
  aiState.value = payload?.state || "idle"
  errorMessage.value = payload?.error || ""
  shouldAutoStart.value = Boolean(payload?.shouldAutoStart)
  suggestions.value = Array.isArray(payload?.suggestions) ? payload.suggestions : []
  window.dispatchEvent(new CustomEvent("ai-suggestions-updated", { detail: { suggestions: suggestions.value } }))
}

function stopPolling() {
  if (pollTimer) {
    window.clearInterval(pollTimer)
    pollTimer = null
  }
}

function startPolling() {
  stopPolling()
  pollTimer = window.setInterval(async () => {
    const ok = await fetchStatus()
    if (!ok || !isAnalyzing.value) {
      stopPolling()
    }
  }, 2000)
}

async function startAnalysis(force = false) {
  isLoading.value = true
  actionError.value = ""

  try {
    const response = await postJson(urlWithParams(props.analyzeUrl, force ? { force: 1 } : {}))
    if (!response.ok) throw new Error("analyze failed")

    const payload = await response.json()
    updateFromPayload(payload)

    if (isAnalyzing.value) {
      startPolling()
    }
  } catch (_) {
    actionError.value = "Unable to start AI analysis."
  } finally {
    isLoading.value = false
  }
}

async function rerunAnalysis() {
  await startAnalysis(true)
}

async function fetchStatus() {
  try {
    const response = await getJson(props.statusUrl)
    if (!response.ok) return false

    const payload = await response.json()
    updateFromPayload(payload)
    return true
  } catch (_) {
    return false
  }
}

async function applySuggestion(suggestion) {
  actionError.value = ""

  try {
    const response = await postJson(suggestion.applyUrl)
    if (!response.ok) throw new Error("apply failed")

    suggestions.value = suggestions.value.filter((s) => s.id !== suggestion.id)
    window.dispatchEvent(new CustomEvent("ai-suggestion-applied", { detail: { stepId: suggestion.stepId, field: suggestion.field, suggestedText: suggestion.suggestedText } }))
    window.dispatchEvent(new CustomEvent("ai-suggestions-updated", { detail: { suggestions: suggestions.value } }))
  } catch (_) {
    actionError.value = "Unable to apply suggestion."
  }
}

async function dismissSuggestion(suggestion) {
  actionError.value = ""

  try {
    const response = await postJson(suggestion.dismissUrl)
    if (!response.ok) throw new Error("dismiss failed")

    suggestions.value = suggestions.value.filter((s) => s.id !== suggestion.id)
    window.dispatchEvent(new CustomEvent("ai-suggestions-updated", { detail: { suggestions: suggestions.value } }))
  } catch (_) {
    actionError.value = "Unable to dismiss suggestion."
  }
}

async function applyAll() {
  if (!canApplyAll.value) return

  isApplyingAll.value = true
  actionError.value = ""
  const pendingSuggestions = [...suggestions.value]

  try {
    const response = await postJson(props.applyAllUrl)
    if (!response.ok) throw new Error("apply-all failed")

    const payload = await response.json()
    updateFromPayload(payload)
    pendingSuggestions.forEach((s) => {
      window.dispatchEvent(new CustomEvent("ai-suggestion-applied", { detail: { stepId: s.stepId, field: s.field, suggestedText: s.suggestedText } }))
    })
  } catch (_) {
    actionError.value = "Unable to apply all suggestions."
  } finally {
    isApplyingAll.value = false
  }
}

async function handleShow() {
  actionError.value = ""

  const ok = await fetchStatus()
  if (!ok) {
    await startAnalysis()
    return
  }

  if (isAnalyzing.value) {
    startPolling()
    return
  }

  if (shouldAutoStart.value) {
    await startAnalysis()
  }
}

function handleHide() {
  stopPolling()
}

onMounted(() => {
  offcanvasElement = document.getElementById(props.offcanvasId)
  if (!offcanvasElement) return

  offcanvasElement.addEventListener("show.bs.offcanvas", handleShow)
  offcanvasElement.addEventListener("hide.bs.offcanvas", handleHide)
})

onBeforeUnmount(() => {
  stopPolling()

  if (!offcanvasElement) return

  offcanvasElement.removeEventListener("show.bs.offcanvas", handleShow)
  offcanvasElement.removeEventListener("hide.bs.offcanvas", handleHide)
})
</script>

<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvasId" :aria-labelledby="`${offcanvasId}Label`">
    <div class="offcanvas-header d-block">
      <div class="d-flex align-items-start justify-content-between gap-2">
        <div class="d-flex align-items-center gap-2">
          <h2 class="offcanvas-title h5 mb-0" :id="`${offcanvasId}Label`">
            <span class="fa fa-atom turboguide-gradient-icon me-1" aria-hidden="true"></span>
            {{ titleLabel }}
          </h2>
          <span class="badge text-bg-light border">{{ stateLabel }}</span>
        </div>
        <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>

      <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
        <button
          type="button"
          class="btn btn-sm btn-outline-primary"
          :disabled="!canRerun"
          @click.prevent="rerunAnalysis"
        >
          {{ rerunLabel }}
        </button>
        <button
          type="button"
          class="btn btn-sm btn-primary"
          :disabled="!canApplyAll"
          @click.prevent="applyAll"
        >
          <span v-if="isApplyingAll" class="fa fa-spinner fa-spin me-1" aria-hidden="true"></span>
          {{ applyAllLabel }}
        </button>
      </div>
    </div>

    <div class="offcanvas-body ai-analysis-offcanvas">
      <div v-if="actionError" class="alert alert-danger py-2" role="alert">
        {{ actionError }}
      </div>

      <div v-if="isLoading || isAnalyzing" class="d-flex align-items-center gap-2 text-muted py-3" aria-live="polite">
        <span class="fa fa-spinner fa-spin" aria-hidden="true"></span>
        <span>{{ loadingLabel }}</span>
      </div>

      <div v-else-if="aiState === 'failed'" class="alert alert-danger" role="alert">
        <strong>{{ stateFailedLabel }}</strong>
        <p class="mb-0 mt-1">{{ errorMessage || "Analysis failed." }}</p>
      </div>

      <div v-else-if="suggestions.length === 0" class="text-muted py-2">
        {{ noSuggestionsLabel }}
      </div>

      <div v-else class="vstack gap-2 ai-analysis-suggestion-list">
        <div v-for="suggestion in suggestions" :key="suggestion.id" class="card shadow-sm">
          <div class="card-body p-3">
            <strong class="small d-block mb-2">
              {{ stepLabel }} {{ suggestion.stepNumber }}
              <span class="badge text-bg-secondary ms-1">{{ suggestion.field }}</span>
            </strong>

            <div class="small mb-2">
              <div class="text-muted mb-1">{{ currentTextLabel }}</div>
              <div class="border rounded p-2 bg-light-subtle ai-analysis-offcanvas__text">{{ suggestion.currentText }}</div>
            </div>

            <div class="small mb-3">
              <div class="text-muted mb-1">{{ suggestedTextLabel }}</div>
              <div class="border rounded p-2 ai-analysis-offcanvas__text">{{ suggestion.suggestedText }}</div>
            </div>

            <div class="d-flex gap-2">
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click.prevent="applySuggestion(suggestion)"
              >
                {{ applyLabel }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click.prevent="dismissSuggestion(suggestion)"
              >
                {{ dismissLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
